import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, KeyRound } from "lucide-react";
import { Card, PageHeader } from "../components/ui";
import { api } from "../lib/api";
import { Link } from "../lib/router";

function parseList(value: string | null): string[] {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function parseCredenciais(value: string | null): { rotulo: string; usuario: string; senha: string }[] {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function SimNao({ value }: { value: boolean | null }) {
  if (value === null || value === undefined) return <span className="text-slate-400">Não informado</span>;
  return value ? <span className="text-emerald-600">Sim</span> : <span className="text-red-600">Não</span>;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</p>
      <div className="mt-0.5 text-sm text-slate-700">{children || "—"}</div>
    </div>
  );
}

export function ChecklistFormatacaoDetailPage({ id }: { id: string }) {
  const checklist = useQuery({
    queryKey: ["checklist-formatacao", id],
    queryFn: async () => {
      const rows = await api.getChecklistFormatacao(Number(id));
      return rows[0] ?? null;
    },
  });

  const data = checklist.data;

  return (
    <div className="page-stack max-w-[960px]">
      <PageHeader
        eyebrow="Equipe Mira Sistemas"
        title={data ? `Checklist #${data.Id}` : "Checklist de formatação"}
        description="Conferência do checklist preenchido pelo técnico."
        actions={
          <Link to="/checklist-formatacao/lista" className="button button--secondary">
            <ArrowLeft className="size-4" />
            Voltar à lista
          </Link>
        }
      />

      {checklist.isLoading && <p className="text-sm text-slate-500">Carregando...</p>}
      {checklist.isError && <p className="text-sm text-red-600">Não foi possível carregar este checklist.</p>}

      {data && (
        <div className="space-y-5">
          <Card className="form-section">
            <div className="form-section-heading">
              <span>01</span>
              <div>
                <h2>Identificação do atendimento</h2>
              </div>
            </div>
            <div className="form-grid">
              <Field label="Data do atendimento">
                {new Date(data.DataAtendimento).toLocaleDateString("pt-BR")}
              </Field>
              <Field label="Técnico responsável">{data.Tecnico}</Field>
              <Field label="Chamado">{data.ChamadoId ? `#${data.ChamadoId}` : null}</Field>
              <Field label="Empresa atendida">{data.EmpresaAtendida}</Field>
              <Field label="Usuário / equipamento">{data.UsuarioEquipamento}</Field>
              <Field label="Marca / modelo">{data.MarcaModelo}</Field>
              <Field label="Número de série">{data.NumeroSerie}</Field>
              <Field label="Patrimônio">{data.Patrimonio}</Field>
            </div>
            <div className="mt-4">
              <Field label="Motivo da formatação">{data.MotivoFormatacao}</Field>
            </div>
          </Card>

          <Card className="form-section">
            <div className="form-section-heading">
              <span>02</span>
              <div>
                <h2><KeyRound className="inline size-[18px]" /> Usuários e senhas configurados</h2>
              </div>
            </div>
            {parseCredenciais(data.Credenciais).length === 0 ? (
              <p className="text-sm text-slate-400">Nenhuma credencial registrada.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="ticket-table">
                  <thead>
                    <tr>
                      <th>Identificação</th>
                      <th>Usuário</th>
                      <th>Senha</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parseCredenciais(data.Credenciais).map((item, index) => (
                      <tr key={index}>
                        <td>{item.rotulo}</td>
                        <td>{item.usuario}</td>
                        <td className="font-mono">{item.senha || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <Card className="form-section">
            <div className="form-section-heading">
              <span>03</span>
              <div>
                <h2>Backup antes da formatação</h2>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Backup realizado">
                <SimNao value={data.BackupRealizado} />
              </Field>
              <Field label="Onde foi salvo">{data.BackupLocal}</Field>
            </div>
            <div className="mt-4">
              <Field label="Itens incluídos no backup">
                {[...parseList(data.BackupItens), data.BackupOutro].filter(Boolean).join(", ")}
              </Field>
            </div>
          </Card>

          <Card className="form-section">
            <div className="form-section-heading">
              <span>04</span>
              <div>
                <h2>Instalação do sistema</h2>
              </div>
            </div>
            <div className="form-grid">
              <Field label="Sistema operacional">{data.SistemaOperacional}</Field>
            </div>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <Field label="Windows ativado"><SimNao value={data.AtivacaoWindows} /></Field>
              <Field label="Windows Update executado"><SimNao value={data.WindowsUpdateExecutado} /></Field>
              <Field label="Drivers atualizados"><SimNao value={data.DriversAtualizados} /></Field>
              <Field label="Antivírus instalado"><SimNao value={data.AntivirusInstalado} /></Field>
            </div>
            <div className="mt-4">
              <Field label="Programas reinstalados">
                {[...parseList(data.ProgramasInstalados), data.ProgramasOutro].filter(Boolean).join(", ")}
              </Field>
            </div>
          </Card>

          <Card className="form-section">
            <div className="form-section-heading">
              <span>05</span>
              <div>
                <h2>Restauração e configuração</h2>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="E-mail configurado"><SimNao value={data.EmailConfigurado} /></Field>
              <Field label="Dados restaurados"><SimNao value={data.DadosRestaurados} /></Field>
              <Field label="Impressoras/rede mapeadas"><SimNao value={data.ImpressorasMapeadas} /></Field>
              <Field label="Usuário de domínio configurado"><SimNao value={data.UsuarioDominioConfigurado} /></Field>
            </div>
          </Card>

          <Card className="form-section">
            <div className="form-section-heading">
              <span>06</span>
              <div>
                <h2>Testes finais e entrega</h2>
              </div>
            </div>
            <Field label="Testes realizados">
              {[...parseList(data.TestesFinais), data.TestesOutro].filter(Boolean).join(", ")}
            </Field>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <Field label="Limpeza física realizada"><SimNao value={data.LimpezaFisicaRealizada} /></Field>
              <Field label="Cliente confirmou funcionamento"><SimNao value={data.ClienteConfirmouFuncionamento} /></Field>
            </div>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <Field label="Responsável pela entrega">{data.ResponsavelEntrega}</Field>
              <Field label="Registrado em">{new Date(data.DataCriacao).toLocaleString("pt-BR")}</Field>
            </div>
            <div className="mt-4">
              <Field label="Observações">{data.Observacoes}</Field>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
